import React from 'react';
import type { ProcessedClient } from '../../../utils/topClientsUtils';
import { formatRelationshipDuration, fmtUSD, fmtMoney } from '../../../utils/topClientsUtils';

interface ClientRankingListProps {
  clients: ProcessedClient[];
  totalRevenue: number;
  isFYQuarterMode: boolean;
  topN: number;
  periodLabel: string;
}

// ─── Rank visual config ───────────────────────────────────────────────────────
function getRankStyles(index: number): { badge: string; bar: string } {
  switch (index) {
    case 0: return { badge: 'bg-amber-100 text-amber-700', bar: 'bg-amber-400' };
    case 1: return { badge: 'bg-slate-200 text-slate-600', bar: 'bg-slate-400' };
    case 2: return { badge: 'bg-orange-100 text-orange-700', bar: 'bg-orange-400' };
    default: return { badge: 'bg-slate-100 text-slate-500', bar: 'bg-slate-200' };
  }
}

// ─── Single client row ────────────────────────────────────────────────────────
const ClientRow: React.FC<{
  client: ProcessedClient;
  index: number;
  totalRevenue: number;
  isFYQuarterMode: boolean;
}> = ({ client, index, totalRevenue, isFYQuarterMode }) => {
  const { badge, bar } = getRankStyles(index);
  const percentage = totalRevenue > 0 ? (client.revenue / totalRevenue) * 100 : 0;
  const isUSD = client.client_base_currency === 'USD' || client.revenue_client_base <= 0;
  const durationText = client.relationship_days > 0
    ? formatRelationshipDuration(client.relationship_days)
    : '';

  const firstYear = client.first_invoice_date
    ? new Date(client.first_invoice_date).getFullYear()
    : null;
  const lastYear = client.last_invoice_date
    ? new Date(client.last_invoice_date).getFullYear()
    : null;

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <div className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
        {/* Left accent bar */}
        <div className={`w-0.5 self-stretch rounded-full flex-none ${bar}`} />

        {/* Rank badge */}
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold flex-none shrink-0 ${badge}`}>
          #{index + 1}
        </div>

        {/* Client info */}
        <div className="flex-1 min-w-0">
          {/* Name + currency badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-800 leading-tight">
              {client.contact_name.trim() || `Client ${client.contact_id}`}
            </p>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-600 border border-slate-200 shrink-0">
              {client.client_base_currency}
            </span>
          </div>

          {/* Relationship pills */}
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {firstYear != null && lastYear != null && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                {firstYear} – {lastYear}
              </span>
            )}
            {durationText && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                {durationText}
              </span>
            )}
          </div>
        </div>

        {/* Revenue + meta */}
        <div className="text-right flex-none min-w-[7.5rem] shrink-0">
          {isUSD ? (
            <p className="text-sm font-bold text-slate-900 tabular-nums">{fmtUSD(client.revenue)}</p>
          ) : (
            <>
              <p className="text-sm font-bold text-slate-900 tabular-nums leading-tight">
                {fmtUSD(client.revenue)}{' '}
                <span className="text-slate-500 font-semibold text-[11px]">USD</span>
              </p>
              <p className="text-[11px] text-slate-500 tabular-nums leading-tight mt-0.5">
                {client.client_base_currency} {fmtMoney(client.revenue_client_base)}
              </p>
            </>
          )}

          <div className="mt-1 flex flex-col items-end gap-0.5">
            <p className="text-[11px] text-slate-500">
              {percentage.toFixed(1)}% of total
            </p>
            {!isFYQuarterMode && client.rankValue != null && (
              <span className="text-[11px] text-slate-400">Rank #{client.rankValue}</span>
            )}
            {isFYQuarterMode && client.fy_label && (
              <span className="text-[11px] text-slate-400">{client.fy_label}</span>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-3">
        <div className="w-full bg-slate-100 rounded-full h-1.5">
          <div
            className="bg-gradient-to-r from-blue-500 to-violet-500 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(100, percentage)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Main list ────────────────────────────────────────────────────────────────
export const ClientRankingList: React.FC<ClientRankingListProps> = ({
  clients,
  totalRevenue,
  isFYQuarterMode,
  topN,
  periodLabel,
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
    {/* Card header */}
    <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100">
      <div className="w-0.5 h-4 rounded-full bg-blue-500 flex-none" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
        Top {topN} Clients
      </span>
      <span className="text-[10px] text-slate-400">·</span>
      <span className="text-[11px] text-slate-500 font-medium">{periodLabel}</span>
    </div>

    {clients.length === 0 ? (
      <div className="px-4 py-10 text-center text-slate-500 text-sm">No clients for selected period</div>
    ) : (
      <div>
        {clients.map((client, index) => (
          <ClientRow
            key={client.contact_id}
            client={client}
            index={index}
            totalRevenue={totalRevenue}
            isFYQuarterMode={isFYQuarterMode}
          />
        ))}
      </div>
    )}
  </div>
);

export default ClientRankingList;
